import React from 'react';
import { useOutreach } from '../../context/OutreachContext';
import { useLinkedIn } from '../../context/LinkedInContext';
import SafeIcon from '../../common/SafeIcon';
import SafeChart from '../common/SafeChart'; // Corrected path

const TrackingDashboard = () => {
  const { clickEvents, leadCaptures } = useOutreach();
  const { prospects = [], tasks = [] } = useLinkedIn();

  const totalProspects = prospects.length;
  const openTasks = tasks.filter(task => task.status === 'Åpen').length;
  const completedTasks = tasks.filter(task => task.status === 'Utført').length;
  const skippedTasks = tasks.filter(task => task.status === 'Hoppet over').length;

  const statuses = ['Ny', 'Klar for kontakt', 'Kontaktet', 'Dialog', 'Møte booket', 'Avvist'];
  const statusCounts = statuses.reduce((result, status) => ({
    ...result,
    [status]: prospects.filter(prospect => prospect.status === status).length
  }), {});

  const researchProspects = prospects.filter(prospect => (
    prospect.title === 'Research' || prospect.full_name === 'Finn kontaktperson'
  ));
  const researchProspectIds = new Set(researchProspects.map(prospect => prospect.id));
  const researchTasks = tasks.filter(task => (
    task.type === 'Research' || researchProspectIds.has(task.prospect_id)
  ));
  const openResearchTasks = researchTasks.filter(task => task.status === 'Åpen').length;
  const completedResearchTasks = researchTasks.filter(task => task.status === 'Utført').length;
  const percentOfProspects = value => totalProspects > 0
    ? `${Math.round((value / totalProspects) * 100)}%`
    : '0%';

  // Metrics
  const totalClicks = clickEvents.filter(e => e.event_type === 'Click').length;
  const totalViews = clickEvents.filter(e => e.event_type === 'PageView').length;
  const totalLeads = leadCaptures.length;
  const conversionRate = totalViews > 0 ? ((totalLeads / totalViews) * 100).toFixed(1) : 0;

  // Chart Data: Clicks per day (Mocked for visual)
  const chartOption = {
    title: { text: 'Aktivitet siste 7 dager' },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'] },
    yAxis: { type: 'value' },
    series: [
      { name: 'Klikk', type: 'line', data: [12, 19, 3, 5, 2, 3, totalClicks], smooth: true, color: '#3b82f6' },
      { name: 'Leads', type: 'bar', data: [1, 2, 0, 1, 0, 0, totalLeads], color: '#10b981' }
    ]
  };

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-black text-slate-900">Tracking Dashboard</h2>

      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
        Tallene gjelder manuell LinkedIn Outreach. Systemet scraper ikke LinkedIn og sender ikke meldinger automatisk.
      </div>

      {totalProspects === 0 ? (
        <div className="p-6 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <h3 className="font-black text-slate-900">Ingen LinkedIn-prospekter ennå</h3>
          <p className="text-sm text-slate-500 mt-2">
            Legg til prospekter manuelt, importer fra CSV eller opprett research-prospects fra BRREG.
          </p>
        </div>
      ) : (
        <>
          <section className="space-y-4">
            <h3 className="text-lg font-black text-slate-900">LinkedIn Outreach-statistikk</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Prospects totalt', value: totalProspects, icon: '👥', color: 'bg-blue-50 text-blue-700' },
                { label: 'Åpne tasks', value: openTasks, icon: '📋', color: 'bg-amber-50 text-amber-700' },
                { label: 'Utførte tasks', value: completedTasks, icon: '✅', color: 'bg-emerald-50 text-emerald-700' },
                { label: 'Hoppet over tasks', value: skippedTasks, icon: '⏭️', color: 'bg-slate-100 text-slate-700' }
              ].map((kpi, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                      <p className="text-3xl font-black text-slate-900 mt-2">{kpi.value}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${kpi.color}`}>
                      <SafeIcon icon={kpi.icon} className="w-6 h-6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-black text-slate-900 mb-4">Prospect-status</h3>
              <div className="space-y-3">
                {statuses.map(status => (
                  <div key={status} className="flex justify-between text-sm">
                    <span className="text-slate-600">{status}</span>
                    <span className="font-black text-slate-900">
                      {statusCounts[status]} ({percentOfProspects(statusCounts[status])})
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="font-black text-slate-900 mb-4">Manuell outreach-trakt</h3>
              <div className="space-y-3">
                {['Prospects totalt', 'Klar for kontakt', 'Kontaktet', 'Dialog', 'Møte booket'].map((label, index) => {
                  const value = index === 0 ? totalProspects : statusCounts[label];
                  return (
                    <div key={label} className="flex justify-between text-sm">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-black text-slate-900">
                        {value} ({percentOfProspects(value)})
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="font-black text-slate-900 mb-4">Research / finn kontaktperson</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { label: 'Research-prospects', value: researchProspects.length, icon: '🔎', color: 'bg-violet-50 text-violet-700' },
                { label: 'Åpne research-tasks', value: openResearchTasks, icon: '🧭', color: 'bg-amber-50 text-amber-700' },
                { label: 'Utførte research-tasks', value: completedResearchTasks, icon: '✅', color: 'bg-emerald-50 text-emerald-700' }
              ].map((kpi, i) => (
                <div key={i} className="bg-slate-50 p-5 rounded-xl border border-slate-100">
                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <SafeIcon icon={kpi.icon} className={`w-6 h-6 ${kpi.color.split(' ')[1]}`} />
                    <p className="text-3xl font-black text-slate-900">{kpi.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      <section>
        <h3 className="text-lg font-black text-slate-900 mb-3">Microsite tracking / V2 – ikke aktivert</h3>
        <p className="text-xs text-slate-400 mb-3">
          Disse tallene er separate fra LinkedIn Outreach og kommer fra V2/in-memory tracking. De er ikke operative LinkedIn Outreach-tall.
        </p>
      </section>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Totale Klikk', value: totalClicks, icon: '🖱️', color: 'bg-blue-50 text-blue-700' },
          { label: 'Sidevisninger', value: totalViews, icon: '👁️', color: 'bg-indigo-50 text-indigo-700' },
          { label: 'Nye Leads', value: totalLeads, icon: '🔥', color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Konvertering', value: `${conversionRate}%`, icon: '📈', color: 'bg-purple-50 text-purple-700' }
        ].map((kpi, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                <p className="text-3xl font-black text-slate-900 mt-2">{kpi.value}</p>
              </div>
              <div className={`p-3 rounded-xl ${kpi.color}`}>
                <SafeIcon icon={kpi.icon} className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <SafeChart option={chartOption} height="300px" />
        </div>
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="font-black text-slate-900 mb-4">Siste Hendelser</h3>
          <div className="space-y-4 max-h-[300px] overflow-y-auto">
            {clickEvents.slice().reverse().slice(0, 10).map(event => (
              <div key={event.id} className="flex items-center gap-3 text-sm p-2 hover:bg-slate-50 rounded-lg">
                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${event.event_type === 'Click' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                  {event.event_type}
                </span>
                <span className="text-slate-500 text-xs font-mono">{event.timestamp.split('T')[1].substring(0, 5)}</span>
                <span className="text-slate-400 text-xs truncate max-w-[150px]">Hash: {event.ip_hash}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingDashboard;